import React, { ChangeEvent, useState } from 'react';

import { useSnackbar } from 'notistack';

import { FileDownload } from '@mui/icons-material';
import { Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';

import {
  useCommitRatePlansBulkUpload,
  useDownloadRatePlansBulkTemplate,
  useGetBulkUploadStatusList,
  useValidateRatePlansBulkUpload,
} from '~/hooks/apis/reservations/useRatePlansBulkUpload';

import PartialCommitDialog from './PartialCommitDialog';
import UploadHistoryTable from './UploadHistoryTable';
import UploadResultsDialog from './UploadResultsDialog';
import ValidationActions from './ValidationActions';
import ValidationErrorsGrid from './ValidationErrorsGrid';
import ValidationSummaryCards from './ValidationSummaryCards';
import { ACCEPTED_FILE_FORMATS } from './constants';
import { BulkUploadStatus, ValidationResult } from './types';

const SECTION_SX = {
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'grey.200',
  borderRadius: 2,
  p: 4,
} as const;

export default function RatePlansBulkUpload() {
  const { enqueueSnackbar } = useSnackbar();

  const [file, setFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState<BulkUploadStatus | null>(null);

  const { data: uploadStatusList = [], isLoading: isLoadingStatus } = useGetBulkUploadStatusList();
  const validateMutation = useValidateRatePlansBulkUpload();
  const commitMutation = useCommitRatePlansBulkUpload();
  const downloadMutation = useDownloadRatePlansBulkTemplate();

  const handleNewUpload = () => {
    setFile(null);
    setValidationResult(null);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      setFile(event.target.files[0]);
      setValidationResult(null);
    }
  };

  const handleUpload = () => {
    if (!file) {
      enqueueSnackbar('Please select a file', { variant: 'warning' });
      return;
    }
    validateMutation.mutate(file, {
      onSuccess: (result) => setValidationResult(result),
      onError: (error) => enqueueSnackbar(`Upload failed: ${error.message}`, { variant: 'error' }),
    });
  };

  const handleCommit = () => {
    if (!validationResult?.batchId) {
      enqueueSnackbar('No validation batch to commit', { variant: 'warning' });
      return;
    }
    commitMutation.mutate(validationResult.batchId, {
      onSuccess: () => {
        enqueueSnackbar('Rate plans committed successfully', { variant: 'success' });
        handleNewUpload();
      },
      onError: (error: Error & { status?: number }) => {
        if (error.status === 422 || error.message?.includes('No valid rows')) {
          enqueueSnackbar('No valid rows to commit. Please fix errors and re-upload.', { variant: 'error' });
          handleNewUpload();
        } else {
          enqueueSnackbar(`Commit failed: ${error.message}`, { variant: 'error' });
        }
      },
    });
  };

  const handleDownloadTemplate = (format: 'xlsx' | 'csv') => {
    downloadMutation.mutate(format, {
      onError: (error) => enqueueSnackbar(`Failed to download template: ${error.message}`, { variant: 'error' }),
    });
  };

  return (
    <Box mt={1}>
      <Paper elevation={0} sx={SECTION_SX}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" color="text.primary">
            Bulk Upload Rate Plans
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FileDownload fontSize="small" />}
              onClick={() => handleDownloadTemplate('xlsx')}
            >
              Template XLSX
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownload fontSize="small" />}
              onClick={() => handleDownloadTemplate('csv')}
            >
              Template CSV
            </Button>
          </Stack>
        </Stack>

        <Typography variant="body1" color="text.secondary" mb={3}>
          Upload multiple rate plans at once using a CSV or Excel file
        </Typography>

        {validationResult ? (
          <Box>
            <Box mt={3}>
              <ValidationSummaryCards validationResult={validationResult} />
            </Box>
            <ValidationErrorsGrid validationResult={validationResult} />
            <ValidationActions
              validationResult={validationResult}
              isCommitting={commitMutation.isPending}
              onCommit={handleCommit}
              onNewUpload={handleNewUpload}
              onConfirmPartialCommit={() => setIsConfirmOpen(true)}
            />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Box>
              <TextField
                type="file"
                onChange={handleFileChange}
                disabled={validateMutation.isPending}
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: ACCEPTED_FILE_FORMATS }}
                fullWidth
              />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Supported formats: CSV, XLS, XLSX
              </Typography>
            </Box>
            <Box>
              <Button variant="contained" onClick={handleUpload} disabled={!file || validateMutation.isPending}>
                {validateMutation.isPending && <CircularProgress size={20} sx={{ mr: 1 }} />}
                {validateMutation.isPending ? 'Validating...' : 'Upload & Validate'}
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>

      <Paper elevation={0} sx={{ ...SECTION_SX, mt: 3 }}>
        <Typography variant="h5" color="text.primary" mb={2}>
          Upload History
        </Typography>
        <UploadHistoryTable
          uploads={uploadStatusList}
          isLoading={isLoadingStatus}
          onShowResults={(upload) => setSelectedUpload(upload)}
        />
      </Paper>

      <UploadResultsDialog open={!!selectedUpload} onClose={() => setSelectedUpload(null)} upload={selectedUpload} />

      <PartialCommitDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onCommit={handleCommit}
        validationResult={validationResult}
      />
    </Box>
  );
}
